import type { OpaqueCursorPagination } from './authorization.types';
import type { OpaqueCursorSearchRequest } from './omnisearch.types';
import type {
    LegalDocumentAssignmentScope,
    LegalDocumentPurpose,
    Status,
} from '@pillar-portal/database/dist/frontend';
import type { SystemDisplayTimeZone } from './timezone.utils';

export interface LegalDocumentListItem {
    legal_document_uuid: string;
    legal_document_title: string;
    legal_document_version: string;
    status: Status;
    legal_document_content_type: string;
    in_force_start: string; // ISO timestamp
    in_force_end: string | null; // ISO timestamp; null means no end date
    legal_document_purpose: LegalDocumentPurpose;
    acceptance_validity_duration_days: number | null;
    content_sha256: string;
    source_filename?: string;
    content_length_bytes: number;
    created_ts: string;
    updated_ts: string | null;
}

export interface LegalDocumentsSearchRequest extends OpaqueCursorSearchRequest {}

/** The Legal Documents list; deleted documents are listed only when asked (FEAT-267). */
export interface LegalDocumentsListRequest extends LegalDocumentsSearchRequest {
    include_deleted?: boolean;
}

export interface SearchLegalDocumentsPageResponse {
    legal_documents: LegalDocumentListItem[];
    pagination: OpaqueCursorPagination;
    display_timezone: SystemDisplayTimeZone;
}

/**
 * Normalized title used to match user agreements across the Organization
 * hierarchy: a nearer assignment overrides an ancestor's or the default one
 * with the same key, and documents with different keys all apply.
 */
export function legalDocumentTitleKey(title: string): string {
    return title.trim().replace(/\s+/g, ' ').toLocaleLowerCase('en-US');
}

export interface CreateLegalDocumentRequest {
    legal_document_title: string;
    legal_document_version: string;
    legal_document_text: string;
    legal_document_content_type: string;
    in_force_start: string; // ISO timestamp
    in_force_end?: string | null; // ISO timestamp; null/undefined means no end date
    legal_document_purpose: LegalDocumentPurpose;
    acceptance_validity_duration_days?: number | null;
    source_filename?: string;
}

interface LegalDocumentAssignmentPublicBase {
    legal_document_assignment_uuid: string;
    legal_document: LegalDocumentListItem;
    status: Status;
    in_force_start: string;
    in_force_end: string | null;
    created_ts: string;
    assigned_by_user_uuid: string;
    revocation: {
        revoked_ts: string;
        revoked_by_user_uuid: string;
        revoke_reason: string | null;
    } | null;
}

// Discriminator: scope
export type LegalDocumentAssignmentPublic =
    | (LegalDocumentAssignmentPublicBase & {
          scope: Extract<LegalDocumentAssignmentScope, 'DEFAULT'>;
          organization: null;
      })
    | (LegalDocumentAssignmentPublicBase & {
          scope: Extract<LegalDocumentAssignmentScope, 'ORGANIZATION'>;
          organization: {
              organization_uuid: string;
              organization_name: string;
          };
      });

interface CreateLegalDocumentAssignmentRequestBase {
    legal_document_uuid: string;
    in_force_start: string;
    in_force_end?: string | null;
}

// Discriminator: scope
export type CreateLegalDocumentAssignmentRequest =
    | (CreateLegalDocumentAssignmentRequestBase & {
          scope: Extract<LegalDocumentAssignmentScope, 'DEFAULT'>;
          organization_uuid?: never;
          supersedes_default: false;
      })
    | (CreateLegalDocumentAssignmentRequestBase & {
          scope: Extract<LegalDocumentAssignmentScope, 'ORGANIZATION'>;
          organization_uuid: string;
          supersedes_default: boolean;
      });

export interface SearchLegalDocumentAssignmentsPageResponse {
    legal_document_assignments: LegalDocumentAssignmentPublic[];
    pagination: OpaqueCursorPagination;
    display_timezone: SystemDisplayTimeZone;
}

interface ApplicableTermsResultBase {
    role_assignment_uuid: string;
}

/** One user agreement that applies to a RoleAssignment, with its acceptance state. */
export interface ApplicableUserAgreement {
    legal_document_assignment: LegalDocumentAssignmentPublic;
    accepted: boolean;
}

// Discriminator: coverage_state
export type ApplicableTermsResult =
    | (ApplicableTermsResultBase & {
          coverage_state: 'exempt';
      })
    // No user agreement resolves for the RoleAssignment. Nothing can be accepted,
    // so the RoleAssignment is not gated; administrators must repair the configuration.
    | (ApplicableTermsResultBase & {
          coverage_state: 'not_configured';
      })
    // Every applicable user agreement is accepted.
    | (ApplicableTermsResultBase & {
          coverage_state: 'covered';
          user_agreements: ApplicableUserAgreement[];
      })
    // At least one applicable user agreement is not accepted yet.
    | (ApplicableTermsResultBase & {
          coverage_state: 'acceptance_required';
          user_agreements: ApplicableUserAgreement[];
      });

/** The first user agreement, in title order, that still needs acceptance. */
export function firstPendingUserAgreement(
    result: Extract<ApplicableTermsResult, { coverage_state: 'acceptance_required' }>
): LegalDocumentAssignmentPublic {
    const pending = result.user_agreements.find((agreement) => !agreement.accepted);
    if (pending === undefined) {
        throw new Error('acceptance_required coverage has no pending user agreement');
    }
    return pending.legal_document_assignment;
}

export interface AcceptApplicableTermsRequest {
    role_assignment_uuid: string;
    legal_document_assignment_uuid: string;
    legal_document_uuid: string;
}

interface OrganizationAgreementStatusBase {
    legal_document_assignment_uuid: string;
    organization: {
        organization_uuid: string;
        organization_name: string;
    };
    legal_document: LegalDocumentListItem;
    latest_valid_acceptance: {
        organization_legal_document_acceptance_uuid: string;
        signer_authority_confirmed: boolean;
        accepted_ts: string;
        accepted_by_user_uuid: string;
        accepted_by_role_assignment_uuid: string;
    } | null;
}

// Discriminator: validity_kind
export type OrganizationAgreementStatus =
    | (OrganizationAgreementStatusBase & {
          validity_kind: 'indefinite';
      })
    | (OrganizationAgreementStatusBase & {
          validity_kind: 'finite';
          acceptance_validity_duration_days: number;
      });

export interface SearchOrganizationAgreementsPageResponse {
    organization_agreements: OrganizationAgreementStatus[];
    pagination: OpaqueCursorPagination;
    display_timezone: SystemDisplayTimeZone;
}

export interface AcceptOrganizationAgreementRequest {
    legal_document_assignment_uuid: string;
    /** The signer confirms being authorized to bind the Organization. */
    signer_authority_confirmed: true;
}

export interface CreateLegalDocumentResponse {
    legal_document_uuid: string;
}

export interface GetLegalDocumentResponse {
    legal_document: LegalDocumentListItem & {
        legal_document_text: string;
    };
}

/** The active RoleAssignment's applicable user agreements that are not accepted yet. */
export interface ListPendingRequiredLegalDocumentsResponse {
    legal_documents: LegalDocumentListItem[];
}

export interface AcceptLegalDocumentRequest {
    accepted_role_assignment_uuid?: string;
}

/** Edits a document in place. Existing acceptances keep the text they accepted. */
export interface UpdateLegalDocumentRequest {
    legal_document_title: string;
    legal_document_version: string;
    legal_document_text: string;
    legal_document_content_type: string;
    in_force_start: string;
    in_force_end: string | null;
    source_filename?: string;
}

export interface RevokeLegalDocumentAssignmentRequest {
    revoke_reason?: string;
    /**
     * A user agreement assigned as the platform default from now, in the same
     * step. Required when the revoked assignment is the last platform default
     * in force.
     */
    replacement_legal_document_uuid?: string;
}

/** Revokes every active assignment of one legal document in one step. */
export interface RevokeAllLegalDocumentAssignmentsRequest {
    revoke_reason?: string;
}

export interface RevokeAllLegalDocumentAssignmentsResponse {
    revoked_assignment_count: number;
}

// Discriminator: acceptance_kind
/**
 * An internal administrator's soft deletion of an acceptance. The acceptance
 * and its accepted text stay on record but no longer cover anyone.
 */
export interface LegalDocumentAcceptanceWithdrawal {
    withdrawn_ts: string;
    withdrawn_by: { user_uuid: string; primary_email: string };
    withdrawn_by_role_assignment_uuid: string;
}

export type LegalDocumentAcceptanceListItem =
    | {
          acceptance_kind: 'user';
          acceptance_uuid: string;
          accepted_ts: string;
          user: { user_uuid: string; primary_email: string };
          organization: { organization_uuid: string; organization_name: string } | null;
          accepted_text_matches_current: boolean;
          withdrawal: LegalDocumentAcceptanceWithdrawal | null;
      }
    | {
          acceptance_kind: 'organization';
          acceptance_uuid: string;
          accepted_ts: string;
          user: { user_uuid: string; primary_email: string };
          organization: { organization_uuid: string; organization_name: string };
          signer_authority_confirmed: boolean;
          accepted_text_matches_current: boolean;
          withdrawal: LegalDocumentAcceptanceWithdrawal | null;
      };

export interface SearchLegalDocumentAcceptancesPageResponse {
    acceptances: LegalDocumentAcceptanceListItem[];
    pagination: OpaqueCursorPagination;
    display_timezone: SystemDisplayTimeZone;
}

/** The exact text a user accepted, which may differ from the current text. */
export interface GetLegalDocumentAcceptanceResponse {
    acceptance: LegalDocumentAcceptanceListItem;
    legal_document: LegalDocumentListItem;
    accepted_content: {
        legal_document_text: string;
        legal_document_content_type: string;
        content_sha256: string;
    };
}

export type LegalDocumentMutableStatus = Extract<Status, 'ACTIVE' | 'DISABLED'>;

export interface UpdateLegalDocumentStatusRequest {
    status: LegalDocumentMutableStatus;
}

/** Response header set on a request denied because legal acceptance is required. */
export const LEGAL_ACCEPTANCE_REQUIRED_HEADER = 'x-pillar-legal-acceptance-required';
